import type { MarketGroup } from "./instruments";
import type { Preset, Setup } from "../types";

/**
 * The Round index: everything selection needs about every Round in a bank,
 * without a single candle (GRA-152, ADR-0012).
 *
 * A bank is tens of megabytes of candles, and playing one Round needs exactly
 * one Round's candles. The index is the seam that separates the two questions:
 * "which Round should I serve next" is answered from a file small enough to
 * ship on the first paint, and "what does that Round look like" is answered by
 * fetching the one shard it lives in. Everything `matchesFilter`,
 * `selectNextRound` and the daily challenge's ranking read is here; nothing
 * else is.
 *
 * The wire format is deliberately tabular rather than a list of objects: the
 * repeated parts (instrument, market group, setup label, shard name) are
 * interned into small header tables and referenced by integer, so the per-Round
 * cost is an id, a length, and four small numbers. That is what keeps the daily
 * index around a tenth of a megabyte gzipped instead of a megabyte, and the
 * whole point of the index is that it is cheap enough to always load first.
 */

/** Bumped whenever the wire format changes shape; an older file is rejected. */
export const ROUND_INDEX_VERSION = 1;

/** How a preset's barrier outcome is spelled in a row's barrier string. */
const BARRIER_CODE: Record<string, "up" | "down" | "time"> = {
  u: "up",
  d: "down",
  t: "time",
};

/** The presets a barrier string spells, in order. */
export const INDEX_PRESET_ORDER: Preset[] = ["easy", "normal", "hard"];

/** One instrument, as the index header interns it. */
export type IndexInstrument = { id: string; name: string; group: MarketGroup };

/**
 * One Round's entry. Shaped so it satisfies `RoundFilter` matching directly
 * (`lib/rounds.ts` `matchesFilter` reads `instrumentId`, `contextLength` and
 * `resolutions`), which is what lets free-play selection run against the index
 * and against a fully loaded bank with the same code.
 */
export type RoundIndexEntry = {
  id: string;
  instrumentId: string;
  instrumentName: string;
  marketGroup: MarketGroup;
  /** How many Context candles the Round carries (the eligibility filter). */
  contextLength: number;
  /** Reveal-only dates, carried so a future history view needs no shard. */
  dateRange: { start: string; end: string };
  /** The Context's setup label, absent on a bank built before GRA-141. */
  setup?: Setup;
  /**
   * Per-preset barrier outcome, under the field name a Round uses so one
   * `matchesFilter` serves both. Absent on the Next-Candle banks, which have
   * no barriers to report.
   */
  resolutions?: Record<Preset, { barrier: "up" | "down" | "time" }>;
  /** The shard file this Round's candles live in, relative to the bank dir. */
  shard: string;
};

/** A parsed index: the entries plus the header tables the UI reads. */
export type RoundIndex = {
  /** Every Round in the bank, in the order the pipeline emitted them. */
  entries: RoundIndexEntry[];
  /** The instruments the bank actually contains, for the markets list. */
  instruments: IndexInstrument[];
  /** Every shard file, in order. */
  shards: string[];
};

/**
 * One row on the wire:
 * `[id, instrumentIndex, contextLength, setupIndex, barriers, shardIndex, start, end]`.
 * `setupIndex` is -1 for "no setup"; `barriers` is "" for a Next-Candle bank.
 */
export type RoundIndexRow = [
  string,
  number,
  number,
  number,
  string,
  number,
  string,
  string,
];

/** The index file as written and fetched. */
export type RoundIndexFile = {
  version: number;
  /** The bank this indexes ("rounds", "weekly", "monthly"). */
  basename: string;
  count: number;
  shards: string[];
  instruments: IndexInstrument[];
  /** Distinct setup labels, referenced by row index. */
  setups: Setup[];
  rounds: RoundIndexRow[];
};

function isRecord(x: unknown): x is Record<string, unknown> {
  return typeof x === "object" && x !== null;
}

function isString(x: unknown): x is string {
  return typeof x === "string";
}

function isIndexInstrument(x: unknown): x is IndexInstrument {
  return isRecord(x) && isString(x.id) && isString(x.name) && isString(x.group);
}

function isRow(x: unknown): x is RoundIndexRow {
  return (
    Array.isArray(x) &&
    x.length === 8 &&
    isString(x[0]) &&
    typeof x[1] === "number" &&
    typeof x[2] === "number" &&
    typeof x[3] === "number" &&
    isString(x[4]) &&
    typeof x[5] === "number" &&
    isString(x[6]) &&
    isString(x[7])
  );
}

function decodeBarriers(
  code: string,
): Record<Preset, { barrier: "up" | "down" | "time" }> | undefined {
  if (code.length !== INDEX_PRESET_ORDER.length) return undefined;
  const out = {} as Record<Preset, { barrier: "up" | "down" | "time" }>;
  for (let i = 0; i < INDEX_PRESET_ORDER.length; i += 1) {
    const barrier = BARRIER_CODE[code[i]];
    if (!barrier) return undefined;
    out[INDEX_PRESET_ORDER[i]] = { barrier };
  }
  return out;
}

/**
 * Parse and validate an index file, dropping any row that does not resolve
 * (an out-of-range instrument or shard reference, a malformed tuple). Returns
 * null for anything that is not an index of a version this build understands,
 * which is what makes a missing or stale `*-index.json` surface as the
 * "Couldn't load rounds" screen rather than an empty game.
 */
export function parseRoundIndex(raw: unknown): RoundIndex | null {
  if (!isRecord(raw)) return null;
  if (raw.version !== ROUND_INDEX_VERSION) return null;
  if (!Array.isArray(raw.rounds) || !Array.isArray(raw.shards)) return null;
  if (!Array.isArray(raw.instruments)) return null;

  const shards = raw.shards.filter(isString);
  const instruments = raw.instruments.filter(isIndexInstrument);
  const setups = Array.isArray(raw.setups) ? (raw.setups as Setup[]) : [];

  const entries: RoundIndexEntry[] = [];
  for (const row of raw.rounds) {
    if (!isRow(row)) continue;
    const [id, instrumentIndex, contextLength, setupIndex, barriers, shardIndex, start, end] =
      row;
    const instrument = instruments[instrumentIndex];
    const shard = shards[shardIndex];
    if (!instrument || !shard) continue;
    entries.push({
      id,
      instrumentId: instrument.id,
      instrumentName: instrument.name,
      marketGroup: instrument.group,
      contextLength,
      dateRange: { start, end },
      setup: setups[setupIndex],
      resolutions: barriers === "" ? undefined : decodeBarriers(barriers),
      shard,
    });
  }

  return { entries, instruments, shards };
}

/** The distinct instruments an index actually carries, in header order. */
export function indexMarkets(index: RoundIndex): { id: string; name: string }[] {
  const present = new Set(index.entries.map((entry) => entry.instrumentId));
  return index.instruments
    .filter((instrument) => present.has(instrument.id))
    .map(({ id, name }) => ({ id, name }));
}

/**
 * The index entry a Round would have, derived from the Round itself. This is
 * what lets a test (and `hydrate`, which tests drive with a plain array) run
 * the exact same selection code against a fully loaded bank as against a
 * fetched index, and it is what the "same ids from either source" test in
 * `roundIndex.test.ts` compares.
 */
export function entryForRound(
  round: {
    id: string;
    instrumentId: string;
    instrumentName: string;
    context: unknown[];
    dateRange: { start: string; end: string };
    setup?: Setup;
    resolutions?: Record<Preset, { barrier: "up" | "down" | "time" }>;
  },
  shard: string,
  group: MarketGroup = "indices",
): RoundIndexEntry {
  return {
    id: round.id,
    instrumentId: round.instrumentId,
    instrumentName: round.instrumentName,
    marketGroup: group,
    contextLength: round.context.length,
    dateRange: round.dateRange,
    setup: round.setup,
    resolutions: round.resolutions
      ? {
          easy: { barrier: round.resolutions.easy.barrier },
          normal: { barrier: round.resolutions.normal.barrier },
          hard: { barrier: round.resolutions.hard.barrier },
        }
      : undefined,
    shard,
  };
}

/** The barrier string a Round's resolutions encode to, "" when it has none. */
export function encodeBarriers(
  resolutions: Record<Preset, { barrier: "up" | "down" | "time" }> | undefined,
): string {
  if (!resolutions) return "";
  return INDEX_PRESET_ORDER.map((preset) => {
    const barrier = resolutions[preset]?.barrier;
    return barrier === "up" ? "u" : barrier === "down" ? "d" : "t";
  }).join("");
}
